import React, { useState } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../redux/actions/login/loginAction';

const headerStyle = {
  width: '100%',
  height: 72,
  background: 'linear-gradient(135deg, #2E8B57 0%, #228B22 50%, #32CD32 100%)', // Elegant parrot green gradient
  borderBottom: '1.5px solid #1a5f3a', // Darker green border
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 2.5rem',
  boxSizing: 'border-box',
  fontFamily: "'Poppins', sans-serif",
  boxShadow: '0 4px 20px 0 rgba(46, 139, 87, 0.4)', // Enhanced green shadow
  borderTopLeftRadius: 'var(--radius)',
  borderTopRightRadius: 'var(--radius)',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  // overflow: 'hidden', // Remove or comment out this line
};

// Add subtle overlay for depth
const headerOverlay = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
  pointerEvents: 'none',
};

const logoStyle = {
  display: 'flex',
  alignItems: 'center',
  fontWeight: 700,
  fontSize: 28,
  color: '#ffffff', // Pure white for maximum contrast
  letterSpacing: 1.5,
  justifyContent: 'center',
  flex: 1,
  gap: 12,
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)', // Enhanced text shadow
  position: 'relative',
  zIndex: 2,
};

const menuButtonStyle = {
  background: 'rgba(255, 255, 255, 0.15)', // Enhanced semi-transparent white
  border: '1px solid rgba(255, 255, 255, 0.25)',
  borderRadius: '10px',
  marginRight: 18,
  cursor: 'pointer',
  padding: '10px',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.3s ease',
  backdropFilter: 'blur(10px)',
  position: 'relative',
  zIndex: 2,
};

const headerInnerStyle = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  position: 'relative',
  zIndex: 1,
};

const leftStyle = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
};

const centerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 2,
};

const rightStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flex: 1,
  gap: 18,
};

const iconBtnStyle = {
  background: 'rgba(255, 255, 255, 0.18)', // Enhanced semi-transparent white background
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '10px',
  cursor: 'pointer',
  padding: '10px',
  display: 'flex',
  alignItems: 'center',
  color: '#ffffff', // Pure white for maximum contrast
  fontSize: 26,
  transition: 'all 0.3s ease',
  minWidth: '44px',
  minHeight: '44px',
  justifyContent: 'center',
  backdropFilter: 'blur(10px)',
  position: 'relative',
  zIndex: 2,
};

const GoldDiamondLogo = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none" style={{ display: 'block' }}>
    <polygon points="24,4 44,16 24,44 4,16" fill="#FFD700" stroke="#FFA500" strokeWidth="2" />
    <polygon points="24,4 34,16 24,44 14,16" fill="#FFF8DC" stroke="#FFA500" strokeWidth="2" />
  </svg>
);

const Header = ({ onMenuClick }) => {
  const user = useSelector(state => state.login.user);
  const [showUsername, setShowUsername] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <header style={headerStyle}>
      <div style={headerOverlay}></div>
      <div style={headerInnerStyle}>
        <div style={leftStyle}>
          <button 
            style={menuButtonStyle} 
            onClick={onMenuClick} 
            aria-label="Toggle sidebar"
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.25)';
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 4px 12px rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.15)';
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <MenuIcon fontSize="large" style={{ color: '#ffffff' }} />
          </button>
        </div>
        <div style={centerStyle}>
          <span style={logoStyle}>
            <GoldDiamondLogo /> JewellERP
          </span>
        </div>
        <div style={rightStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 18 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {user && user.username && (
                <span style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#00C853',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 20,
                  marginRight: 4,
                }}>
                  {user.username.charAt(0).toUpperCase()}
                </span>
              )}
              {user && user.username && (
                <span style={{ color: '#fff', fontWeight: 600, fontSize: 17, letterSpacing: 0.5 }}>
                  {user.username.charAt(0).toUpperCase() + user.username.slice(1)}
                  {user.company_code && (
                    <span style={{ color: '#fff', fontWeight: 500, fontSize: 15, marginLeft: 10 }}>
                      [ {user.company_code} ]
                    </span>
                  )}
                </span>
              )}
            </span>
          </div>
          <button 
            style={{ ...iconBtnStyle, display: 'flex', alignItems: 'center', gap: 8 }} 
            title="Logout"
            onClick={handleLogout}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 4px 12px rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.18)';
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <LogoutIcon fontSize="inherit" />
            <span style={{ fontWeight: 600, fontSize: 16, color: '#fff', letterSpacing: 0.5 }}>Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
